/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import "chart.js/auto";

import { Chart } from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { FC, useRef } from "react";
import { Line, Bar } from "react-chartjs-2";

import Colors from "@constants/Colors";
import Loader from "@src/components/shared-containers/Loader";
import { TOTAL_SALES_VIEW } from "@src/pages/Dashboard/RevenueDashBoard/TotalSales/helper";
import {
  DEFAULTSALESVALUE,
  formatNumberWithCommaAndDollar,
  numberFormatter,
} from "@src/constants";
import { GlobalStyles } from "@mui/material";
import { LineChartPropsType } from "./type";
import { StyledLoaderBox, Wrapper } from "./style";

const LineChart: FC<LineChartPropsType> = ({
  loading,
  activeTab,
  time,
  sales,
  labours,
  targetValue,
  noData,
  isQsProperty,
  scrollable,
}) => {
  const ref = useRef();
  Chart.register(annotationPlugin);

  const showScrollGraph = (isQsProperty && sales?.length > 0) || scrollable;

  const currentTime = time?.length > 0 && time?.slice(-1)?.toString();
  const showAnnotations = activeTab === TOTAL_SALES_VIEW.REAL_TIME && !noData;
  const showTimeAnnotations =
    activeTab === TOTAL_SALES_VIEW.REAL_TIME && currentTime;

  const mapDataSets = [];

  if (
    activeTab === TOTAL_SALES_VIEW.REAL_TIME ||
    activeTab === TOTAL_SALES_VIEW.HOUR
  ) {
    mapDataSets.push({
      fill: false,
      lineTension: 0.2,
      borderWidth: 2,
      backgroundColor: Colors.secondarypurple_700,
      borderColor: Colors.secondarypurple_700,
      borderCapStyle: "butt",
      borderDash: [],
      borderJoinStyle: "miter",
      pointBorderColor: Colors.secondarypurple_700,
      pointBackgroundColor: Colors.white,
      pointBorderWidth: 2,
      pointRadius: (ctx: any) => {
        const { labels } = ctx.chart.data;
        const pointArray: number[] = [];
        labels?.map((i: string) => {
          if (i === currentTime) {
            pointArray.push(7);
          } else {
            pointArray.push(0);
          }
          return null;
        });
        return pointArray;
      },
      // Default value When no data Available
      data: sales?.length > 0 ? sales : DEFAULTSALESVALUE,
    });
    if (labours?.length) {
      mapDataSets.push({
        fill: false,
        lineTension: 0.2,
        backgroundColor: Colors.secondarypink_500,
        borderColor: Colors.secondarypink_500,
        borderCapStyle: "butt",
        borderDash: [],
        borderJoinStyle: "miter",
        pointBorderColor: Colors.secondarypink_500,
        pointBackgroundColor: Colors.white,
        pointBorderWidth: 2,
        pointRadius: (ctx: any) => {
          const { labels } = ctx.chart.data;
          const pointArray: number[] = [];
          labels?.map((i: string) => {
            if (i === currentTime) {
              pointArray.push(7);
            } else {
              pointArray.push(0);
            }
            return null;
          });
          return pointArray;
        },
        data: labours,
      });
    }
  } else if (activeTab === TOTAL_SALES_VIEW.DAY_PART) {
    mapDataSets.push({
      type: "bar",
      barThickness: 9,
      maxBarThickness: 9,
      backgroundColor: labours
        ? Colors.secondarypurple_500
        : Colors.secondarypurple,
      data: sales?.length > 0 ? sales : DEFAULTSALESVALUE,
    });
    if (labours?.length) {
      mapDataSets.push({
        type: "bar",
        barThickness: 9,
        maxBarThickness: 9,
        backgroundColor: Colors.secondarypink_500,
        data: labours,
      });
    }
  }
  const nullValue = Math.ceil(time.length * 0.1);
  const arr = new Array(nullValue).fill(null);

  const data: any = {
    labels: activeTab === TOTAL_SALES_VIEW.REAL_TIME ? [...time, ...arr] : time,
    datasets: mapDataSets,
  };

  const lineOptions: any = {
    stacked: showScrollGraph,
    responsive: !showScrollGraph,
    maintainAspectRatio: !showScrollGraph,
    scales: {
      x: {
        border: {
          color:
            sales?.length > 0
              ? Colors?.neutral_gray_400
              : Colors.neutral_gray_300,
          width: 1,
        },
        time: {
          unit: "hour",
        },
        grid: {
          tickColor: Colors?.primarygray_400,
          lineWidth: 1,
          lineHeight: 2,
          drawOnChartArea: false,
          drawTicks: true,
          offset: false,
        },
        ticks: {
          visible: true,
          autoSkip: !showScrollGraph,
          maxTicksLimit: !showScrollGraph && 6,
          source: !showScrollGraph && "auto",
        },
      },
      y: {
        afterFit(scale: any) {
          if (sales?.length === 0) {
            scale.width = 0;
          }
        },
        ticks: {
          crossAlign: "far",
          callback: numberFormatter,
          maxTicksLimit: 5,
        },
        border: {
          display: false,
        },
        beginAtZero: true,
        grid: {
          color: Colors.neutral_gray_300,
          drawTicks: false,
        },
      },
    },
    layout: {
      padding: 0,
    },
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label(context: any) {
            let label = "";
            if (context.raw !== null) {
              label += formatNumberWithCommaAndDollar(context.raw);
            }
            return label;
          },
        },
      },
      annotation: {
        annotations: {
          line1: showAnnotations && {
            type: "line",
            yMin: targetValue,
            yMax: targetValue,
            borderColor: Colors.success_500,
            borderWidth: 2,
            height: "1px",
            border: `0.2px solid ${Colors.success_500}`,
            drawTime: "beforeDatasetsDraw",
          },
          line2: showTimeAnnotations && {
            type: "line",
            xMin: currentTime,
            xMax: currentTime,
            borderColor: Colors.neutral_gray_300,
            borderDash: [10, 10],
            borderWidth: 2,
            drawTime: "beforeDatasetsDraw",
          },
        },
      },
    },
  };

  if (loading) {
    return (
      <StyledLoaderBox>
        <Loader size={80} />
      </StyledLoaderBox>
    );
  }

  if (showScrollGraph) {
    return (
      <>
        <GlobalStyles
          styles={{
            "*::-webkit-scrollbar": {
              width: "0.4em",
              height: "8px",
            },
            "*::-webkit-scrollbar-track": {
              WebkitBoxShadow: `inset 0 0 6px ${Colors.black}`,
              backgroundColor: Colors.primarygray_200,
              boxShadow: Colors.Elevation_2,
            },
            "*::-webkit-scrollbar-thumb": {
              backgroundColor: Colors.primarygray_400,
              outline: "1px solid slategrey",
              borderRadius: "4px",
              cursor: "pointer",
            },
          }}
        />
        <Wrapper>
          <div className="chartWrapper">
            <div className="chartAreaWrapper">
              <Line
                height="300"
                width="1200"
                ref={ref}
                data={data}
                options={lineOptions}
              />
            </div>
          </div>
        </Wrapper>
      </>
    );
  }

  if (
    activeTab === TOTAL_SALES_VIEW.REAL_TIME ||
    activeTab === TOTAL_SALES_VIEW.HOUR
  ) {
    return <Line height="170px" ref={ref} data={data} options={lineOptions} />;
  }

  return <Bar height="170px" ref={ref} data={data} options={lineOptions} />;
};

export default LineChart;
